/**
 * ThemeProvider
 *
 * The main theme provider that combines:
 * 1. **ColorModeProvider** - Dark/light mode management
 * 2. **ChakraProvider** - Chakra UI v3 theme system
 * 3. **Toaster** - Global toast notification system
 *
 * ## Architecture
 *
 * ```
 * PageThemeConfigProvider (app-level context for page colors)
 *   └── ThemeProvider
 *         └── ColorModeProvider (next-themes)
 *               └── ChakraProvider (Chakra UI v3)
 *                     └── App + Toaster
 * ```
 *
 * ## Dynamic Theming
 *
 * Supports custom primary/secondary colors via:
 * 1. URL query params: `?theme-primary=#FF0000&theme-secondary=#00FF00`
 * 2. Page-level config: `getThemeConfig: () => ({ primaryColor: "#FF0000" })`
 *
 * Page-level config takes precedence over URL params.
 *
 * When no custom colors are provided, uses `defaultSystem` which is
 * a static object created at module load time. This is critical for
 * SSR hydration - the same object reference must be used on both
 * server and client to prevent style flicker.
 *
 * ## SSR/Hydration
 *
 * The `defaultSystem` is imported from `theme.ts` and created once.
 * This prevents the hydration bug where styles would disappear because
 * a new theme object was being created on each render.
 *
 * ## Related Files
 *
 * - `theme.ts` - Theme configuration and semantic tokens
 * - `palette-generator.ts` - Color palette generation logic
 * - `ColorModeProvider.tsx` - Dark mode state management
 * - `PageThemeConfigContext.tsx` - Page-level theme config context
 * - `lib/createEmotionCache.ts` - Emotion SSR cache
 * - `pages/_document.tsx` - SSR style extraction
 * - `pages/_app.tsx` - App wrapper that uses this provider
 */
import React, { useMemo } from "react";
import { ChakraProvider, Toaster, Portal } from "@chakra-ui/react";
import { useRouter } from "next/router";
import generateTheme, { defaultSystem } from "@/theme";
import { getActiveToaster } from "@ui-kit/feedback/alerts/Toast/Toast.web";
import ToastAlert from "@ui-kit/feedback/alerts/ToastAlert/ToastAlert.web";
import { ColorModeProvider } from "@/providers/ColorModeProvider";
import { usePageThemeConfig } from "@/providers/PageThemeConfigContext";
import {
  colorToPalette,
  validateColorString,
} from "@shared/theme/utils/palette-generator";
import useNativeAppCheck from "@app/core/src/shared/hooks/platform/useNativeAppCheck";

type ThemeProviderProps = {
  children: React.ReactNode;
  /**
   * Force a specific color mode. Passed to ColorModeProvider.
   * Will be controlled by backend config in the future.
   */
  forcedColorMode?: "light" | "dark";
  /**
   * CSP nonce for inline script injection.
   * Required for Content Security Policy compliance.
   */
  nonce?: string;
};

const ThemeProvider: React.FC<ThemeProviderProps> = ({
  children,
  forcedColorMode,
  nonce,
}) => {
  const router = useRouter();
  const pageThemeConfig = usePageThemeConfig();

  // URL query params (fallback)
  const urlPrimary = validateColorString(router?.query?.["theme-primary"]);
  const urlSecondary = validateColorString(router?.query?.["theme-secondary"]);

  // Page config takes precedence over URL params
  const themePrimary =
    validateColorString(pageThemeConfig?.primaryColor) || urlPrimary;
  const themeSecondary =
    validateColorString(pageThemeConfig?.secondaryColor) || urlSecondary;

  const { isReactNativeApp } = useNativeAppCheck();

  /**
   * Generate the theme system based on the custom colors.
   * If no custom colors are provided, use the default system.
   */
  const system = useMemo(() => {
    if (!themePrimary && !themeSecondary) {
      return defaultSystem;
    }

    const primaryColor = themePrimary
      ? colorToPalette(themePrimary)
      : undefined;
    const secondaryColor = themeSecondary
      ? colorToPalette(themeSecondary)
      : undefined;

    return generateTheme({
      primaryColor,
      secondaryColor,
    });
  }, [themePrimary, themeSecondary]);

  /**
   * Determines the active toaster instance and a key for the Toaster component.
   * The key is used to force remount the Toaster component when switching
   * between webview and web environments, ensuring it properly subscribes to the
   * correct toaster instance.
   */
  const activeToaster = useMemo(() => {
    return {
      toaster: getActiveToaster(),
      key: isReactNativeApp ? "webview" : "web",
    };
  }, [isReactNativeApp]);

  // Only force mode when forceColorMode is explicitly true
  const effectiveForcedMode = pageThemeConfig?.forceColorMode
    ? pageThemeConfig.colorMode
    : forcedColorMode;

  return (
    <ColorModeProvider forcedMode={effectiveForcedMode} nonce={nonce}>
      <ChakraProvider value={system}>
        {children}
        <Portal>
          <Toaster
            toaster={activeToaster.toaster}
            key={activeToaster.key}
            insetInline={{ mdDown: "4" }}
          >
            {(toast) => (
              <ToastAlert
                status={toast.type === "loading" ? "info" : toast.type}
                title={toast.title as string}
                description={toast.description as string}
              />
            )}
          </Toaster>
        </Portal>
      </ChakraProvider>
    </ColorModeProvider>
  );
};

export default ThemeProvider;
